import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { CapTableState } from '../slices/capTableSlice';

// Types for Cap Table API
export interface GenerateCapTableRequest {
    stage: string;
    company: {
        name: string;
        cin?: string;
        state?: string;
        incorporationDate?: string;
        parValue?: string;
        authorizedShares?: string;
        capTableDate?: string;
    };
    founders: Array<{
        name: string;
        shares: number;
        shareClass?: string;
        vesting?: string;
        role?: string;
    }>;
    investors?: Array<{
        name: string;
        type?: string;
        shares: number;
        investment: number;
        pricePerShare?: number;
        shareClass?: string;
        securityType?: string;
    }>;
    esop?: {
        totalPool?: number;
        poolSize?: number;
        allocated?: number;
        strikePrice?: number;
        vestingSchedule?: string;
    };
    fundingRound?: {
        name?: string;
        roundName?: string;
        preMoneyValuation?: number;
        amountRaised?: number;
        pricePerShare?: number;
        date?: string;
    };
    convertibles?: Array<{
        investorName: string;
        instrumentType: string;
        principalAmount: number;
        valuationCap?: number;
        discountRate?: number;
    }>;
}

export interface GenerateCapTableResponse {
    url: string;
    fileName: string;
    s3Key: string;
    stage: string;
    generatedAt: string;
    sheetsGenerated?: string[];
    fileSizeBytes?: number;
}

export interface ValidateCapTableRequest {
    stage: string;
    company?: {
        name?: string;
    };
    founders?: Array<{
        name: string;
    }>;
}

export interface ValidateCapTableResponse {
    valid: boolean;
    errors: string[];
}

export interface GetStagesResponse {
    stages: string[];
}

// Project CRUD Types
export interface CapTableProjectSummary {
    _id: string;
    name: string;
    stage: string;
    status: 'draft' | 'in-progress' | 'completed';
    generatedFile?: {
        s3Url: string;
        fileName: string;
        generatedAt: string;
    };
    createdAt: string;
    updatedAt: string;
}

export interface CapTableProjectFull extends CapTableProjectSummary {
    userId: string;
    capTableData: {
        companyInfo?: any;
        basicsInfo?: any;
        founders?: any[];
        investors?: any[];
        convertibles?: any[];
        esopPool?: any;
        fundingRounds?: any[];
        shareClasses?: any[];
        hasConvertibles?: boolean | null;
        hasEsop?: boolean | null;
        currentSectionIndex?: number;
        completedSections?: string[];
    };
    versions?: Array<{
        s3Url: string;
        s3Key: string;
        fileName: string;
        generatedAt: string;
    }>;
}

export interface CreateCapTableProjectRequest {
    name?: string;
    stage: string;
}

export interface CreateCapTableProjectResponse {
    projectId: string;
    name: string;
    stage: string;
    status: string;
    createdAt: string;
}

export interface UpdateCapTableProjectRequest {
    projectId: string;
    capTableData?: any;
    status?: 'draft' | 'in-progress' | 'completed';
    name?: string;
}

export interface UpdateCapTableProjectResponse {
    projectId: string;
    updatedAt: string;
    status: string;
}

export interface ListCapTableProjectsParams {
    page?: number;
    limit?: number;
    stage?: string;
    status?: string;
}

export interface ListCapTableProjectsResponse {
    data: CapTableProjectSummary[];
    pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
    };
}

// Edit Cap Table Types
export interface EditCapTableRequest {
    projectId: string;
    editRequest: string;
}

export interface EditCapTableResponse {
    updatedSheets: string[];
    document: {
        url: string;
        fileName: string;
        s3Key: string;
        version: number;
    };
    editRequest: string;
    editedAt: string;
}

// Helper function to transform Redux state to API request format
export function transformCapTableStateToRequest(state: CapTableState): GenerateCapTableRequest {
    const { stage, companyInfo, basicsInfo, founders, investors, esopPool, fundingRounds, convertibles } = state;

    // Determine company name based on stage
    const isPreInc = stage === 'pre-incorporation';
    const companyName = isPreInc
        ? basicsInfo.companyName
        : companyInfo.companyName;

    return {
        stage: stage || 'seed',
        company: {
            name: companyName || 'Company',
            cin: companyInfo.cin || undefined,
            state: isPreInc ? basicsInfo.plannedState : companyInfo.stateOfIncorporation,
            incorporationDate: companyInfo.dateOfIncorporation || undefined,
            parValue: isPreInc ? basicsInfo.plannedParValue : companyInfo.parValue,
            authorizedShares: isPreInc ? basicsInfo.plannedAuthorizedShares : companyInfo.authorizedShares,
        },
        founders: founders.map(f => ({
            name: f.name,
            shares: parseInt(f.shares.replace(/,/g, '')) || 0,
            shareClass: f.shareClass || 'Common',
            vesting: f.vestingSchedule || '4-yr, 1-yr cliff',
            role: 'Founder',
        })),
        investors: investors.length > 0 ? investors.map(i => ({
            name: i.name,
            type: i.type,
            shares: parseInt(i.shares.replace(/,/g, '')) || 0,
            investment: parseInt(i.amountInvested.replace(/,/g, '')) || 0,
            pricePerShare: parseFloat(i.pricePerShare.replace(/,/g, '')) || 0,
            shareClass: i.shareClass || 'Preferred',
            securityType: i.instrumentType === 'equity' ? 'CCPS' : i.instrumentType.toUpperCase(),
        })) : undefined,
        esop: {
            totalPool: parseInt(esopPool.poolSize.replace(/,/g, '')) || 0,
            poolSize: parseInt(esopPool.poolSize.replace(/,/g, '')) || 0,
            vestingSchedule: esopPool.vestingSchedule || '4-yr, 1-yr cliff',
        },
        fundingRound: fundingRounds.length > 0 ? {
            name: fundingRounds[0].roundName,
            roundName: fundingRounds[0].roundName,
            preMoneyValuation: parseInt(fundingRounds[0].preMoneyValuation.replace(/,/g, '')) || 0,
            amountRaised: parseInt(fundingRounds[0].amountRaised.replace(/,/g, '')) || 0,
            pricePerShare: parseFloat(fundingRounds[0].pricePerShare.replace(/,/g, '')) || 0,
            date: fundingRounds[0].closingDate,
        } : undefined,
        convertibles: convertibles.length > 0 ? convertibles.map(c => ({
            investorName: c.investorName,
            instrumentType: c.instrumentType,
            principalAmount: parseInt(c.principalAmount.replace(/,/g, '')) || 0,
            valuationCap: parseInt(c.valuationCap.replace(/,/g, '')) || 0,
            discountRate: parseFloat(c.discountRate) || 0,
        })) : undefined,
    };
}

export const capTableApi = createApi({
    reducerPath: 'capTableApi',
    baseQuery: fetchBaseQuery({
        baseUrl: `${process.env.NEXT_PUBLIC_API_URL}/captable`,
        prepareHeaders: (headers, { getState }) => {
            const token = (getState() as any).auth.token || (typeof window !== 'undefined' ? localStorage.getItem('token') : null);
            if (token) {
                headers.set('authorization', `Bearer ${token}`);
            }
            return headers;
        },
    }),
    tagTypes: ['CapTable', 'CapTableProject'],
    endpoints: (builder) => ({
        // ============================================
        // PROJECT CRUD ENDPOINTS
        // ============================================

        // Create a new cap table project
        createCapTableProject: builder.mutation<CreateCapTableProjectResponse, CreateCapTableProjectRequest>({
            query: (data) => ({
                url: '/projects',
                method: 'POST',
                body: data,
            }),
            transformResponse: (response: { success: boolean; data: CreateCapTableProjectResponse }) =>
                response.data,
            invalidatesTags: ['CapTableProject'],
        }),

        // Get a single cap table project
        getCapTableProject: builder.query<CapTableProjectFull, { projectId: string }>({
            query: ({ projectId }) => `/projects/${projectId}`,
            transformResponse: (response: { success: boolean; data: CapTableProjectFull }) =>
                response.data,
            providesTags: (result, error, { projectId }) => [
                { type: 'CapTableProject', id: projectId }
            ],
        }),

        // Update cap table project (auto-save)
        // Note: No invalidatesTags here - the client already has the latest data it just sent.
        // Adding invalidatesTags would cause an infinite loop: update → invalidate → refetch → load to Redux → auto-save → repeat
        updateCapTableProject: builder.mutation<UpdateCapTableProjectResponse, UpdateCapTableProjectRequest>({
            query: ({ projectId, ...data }) => ({
                url: `/projects/${projectId}`,
                method: 'PUT',
                body: data,
            }),
            transformResponse: (response: { success: boolean; data: UpdateCapTableProjectResponse }) =>
                response.data,
        }),

        // List user's cap table projects
        listCapTableProjects: builder.query<ListCapTableProjectsResponse, ListCapTableProjectsParams>({
            query: (params) => ({
                url: '/projects',
                params,
            }),
            transformResponse: (response: { success: boolean; data: CapTableProjectSummary[]; pagination: any }) => ({
                data: response.data,
                pagination: response.pagination,
            }),
            providesTags: ['CapTableProject'],
        }),

        // Delete a cap table project
        deleteCapTableProject: builder.mutation<{ projectId: string; deleted: boolean }, { projectId: string }>({
            query: ({ projectId }) => ({
                url: `/projects/${projectId}`,
                method: 'DELETE',
            }),
            transformResponse: (response: { success: boolean; data: { projectId: string; deleted: boolean } }) =>
                response.data,
            invalidatesTags: ['CapTableProject'],
        }),

        // ============================================
        // GENERATION ENDPOINTS
        // ============================================

        // Get available stages
        getStages: builder.query<GetStagesResponse, void>({
            query: () => '/stages',
            transformResponse: (response: { data?: { stages: string[] }, stages?: string[] }) =>
                response.data || { stages: response.stages || [] },
        }),

        // Validate cap table data
        validateCapTable: builder.mutation<ValidateCapTableResponse, ValidateCapTableRequest>({
            query: (data) => ({
                url: '/validate',
                method: 'POST',
                body: data,
            }),
            transformResponse: (response: { data?: ValidateCapTableResponse } & ValidateCapTableResponse) =>
                response.data || response,
        }),

        // Generate cap table Excel file
        generateCapTable: builder.mutation<GenerateCapTableResponse, GenerateCapTableRequest>({
            query: (data) => ({
                url: '/generate',
                method: 'POST',
                body: data,
            }),
            transformResponse: (response: { data?: GenerateCapTableResponse } & GenerateCapTableResponse) =>
                response.data || response,
        }),

        // Generate cap table for a project (saves result to project)
        generateCapTableForProject: builder.mutation<GenerateCapTableResponse, { projectId: string; data: GenerateCapTableRequest }>({
            query: ({ projectId, data }) => ({
                url: `/projects/${projectId}/generate`,
                method: 'POST',
                body: data,
            }),
            transformResponse: (response: { data?: GenerateCapTableResponse } & GenerateCapTableResponse) =>
                response.data || response,
            invalidatesTags: (result, error, { projectId }) => [
                { type: 'CapTableProject', id: projectId }
            ],
        }),

        // Preview cap table (local file, no S3 upload)
        previewCapTable: builder.mutation<GenerateCapTableResponse, GenerateCapTableRequest>({
            query: (data) => ({
                url: '/preview',
                method: 'POST',
                body: data,
            }),
            transformResponse: (response: { data?: GenerateCapTableResponse } & GenerateCapTableResponse) =>
                response.data || response,
        }),

        // Edit cap table via natural language prompt
        editCapTable: builder.mutation<EditCapTableResponse, EditCapTableRequest>({
            query: ({ projectId, editRequest }) => ({
                url: `/projects/${projectId}/edit`,
                method: 'POST',
                body: { editRequest },
            }),
            transformResponse: (response: { data: EditCapTableResponse }) => response.data,
        }),
    }),
});

export const {
    // Project CRUD hooks
    useCreateCapTableProjectMutation,
    useGetCapTableProjectQuery,
    useLazyGetCapTableProjectQuery,
    useUpdateCapTableProjectMutation,
    useListCapTableProjectsQuery,
    useDeleteCapTableProjectMutation,
    // Generation hooks
    useGetStagesQuery,
    useValidateCapTableMutation,
    useGenerateCapTableMutation,
    useGenerateCapTableForProjectMutation,
    usePreviewCapTableMutation,
    // Edit hooks
    useEditCapTableMutation,
} = capTableApi;
